import Vue from 'vue';
import { BfModal, BfButton, BfDialog } from "components/Butterfly";
import { Form, Field } from "components/forms";
import ToggleCheckbox from "components/widgets/ToggleCheckbox.vue";
import Popper from 'vue-popperjs';
import { widgetNameToLabel, defaultConfig } from 'pages/dashboard/Dashboard.utils';
import { cloneDeep } from 'lodash';
const DashboardWidgetsModal = Vue.extend({
    components: { BfDialog, ToggleCheckbox, Form, BfModal, BfButton, Field, Popper },
    props: {
        visible: { type: Boolean, required: false, default: false },
        widgetConfig: { type: Object, required: false, default: () => ({ overview: [], drilldown: [] }) },
        hasNps: { type: Boolean, required: false, default: false },
        hasSentiment: { type: Boolean, required: false, default: false },
        hasNumericFields: { type: Boolean, required: false, default: false },
        hasDate: { type: Boolean, required: false, default: false },
        allowReset: { type: Boolean, required: false, default: false },
    },
    computed: {
        /** labels to iterate over for table rows */
        tableKeys() {
            return Object.keys(this.tableList);
        },
        /**
        This flattens the passed widgetConfig down to a structure which
        can be used more easily by our <Form> & <Field> components
        */
        tableList() {
            let keys = Array.from(new Set([
                ...this.widgetConfig.overview.map((w) => w.name),
                ...this.widgetConfig.drilldown.map((w) => w.name)
            ]));
            // Don't show the compare widgets in the table, their visibility is
            // based on the visibility of the non-compare widget they're based on.
            // e.g compare-nps-summary is shown if nps-summary is enabled
            keys = keys.filter((k) => !k.startsWith('compare-'));
            const overviewTable = keys.reduce((rows, key) => {
                const tableKey = this.widgetKeyToTableKey(key);
                const overviewIndex = this.widgetConfig.overview?.findIndex(w => w.name == key) ?? -1;
                const drilldownIndex = this.widgetConfig.drilldown?.findIndex(w => w.name == key) ?? -1;
                if (overviewIndex !== -1 || drilldownIndex !== -1) {
                    rows[tableKey] = rows[tableKey] ?? {};
                }
                if (overviewIndex !== -1) {
                    rows[tableKey].overviewKey = key;
                    rows[tableKey].overviewVisible = this.widgetConfig.overview[overviewIndex]?.visible;
                }
                if (drilldownIndex !== -1) {
                    rows[tableKey].drilldownKey = key;
                    rows[tableKey].drilldownVisible = this.widgetConfig.drilldown[drilldownIndex]?.visible;
                }
                return rows;
            }, {});
            return overviewTable;
        },
    },
    methods: {
        isValid(key) {
            const requiredField = {
                'NPS': this.hasNps,
                'Sentiment': this.hasSentiment,
                'Quadrant': this.hasNps || this.hasSentiment || this.hasNumericFields,
                'Timeline': this.hasDate,
                'Emergent Concepts': this.hasDate,
            };
            return requiredField[key] ?? true;
        },
        // this is required due to lack of support for nested objects in
        // our VeeValidate v4 shims.  When we upgrade to Vue3 & VeeValidate v4
        // we shouldn't need this
        // https://vee-validate.logaretm.com/v4/guide/components/nested-objects-and-arrays/
        toggleValue(value, sourceField, view, setFieldValue) {
            let newValue = { ...sourceField.value };
            if (view === 'overview') {
                newValue.overviewVisible = value;
            }
            if (view === 'drilldown') {
                newValue.drilldownVisible = value;
            }
            setFieldValue(sourceField.name, newValue);
        },
        // returns an appropriate label for the rows for each widget
        // if the config key for the widget is the same as the label
        // then it isn't listed here & just returns the key.  Also
        // some widgets are grouped under the same label even though
        // they are technically different widgets on each view.
        // eg. nps-summary & nps-timeline are both 'NPS'
        widgetKeyToTableKey(key) {
            return widgetNameToLabel(key);
        },
        apply(formValues) {
            const config = cloneDeep(this.widgetConfig);
            Object.values(formValues).forEach((tableRow) => {
                const overviewItem = config.overview.find(({ name }) => name === tableRow.overviewKey);
                const drilldownItem = config.drilldown.find(({ name }) => name === tableRow.drilldownKey);
                if (overviewItem)
                    overviewItem.visible = !!tableRow.overviewVisible;
                if (drilldownItem)
                    drilldownItem.visible = !!tableRow.drilldownVisible;
            });
            this.$emit('widgets-updated', config);
        },
        close() {
            this.$emit('close');
        },
        clearConfig(row) {
            const config = cloneDeep(this.widgetConfig);
            const replaceConfig = (key, widgetName) => {
                const blankConfig = defaultConfig().widgets[key];
                const blankWidget = blankConfig.find(({ name }) => name === widgetName);
                if (blankWidget) {
                    config[key] = config[key].map((item) => item.name === widgetName
                        ? blankWidget
                        : item);
                }
            };
            row.overviewKey &&
                replaceConfig('overview', row.overviewKey);
            row.drilldownKey &&
                replaceConfig('drilldown', row.drilldownKey);
            this.$emit('widgets-updated', config);
        },
    }
});
export default DashboardWidgetsModal;
